<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type { IUiMultiselectOption, IUiMultiselectProps } from './UiMultiselect.types'

const props = withDefaults(defineProps<IUiMultiselectProps>(), {
  disabled: false,
})

const modelValue = defineModel<(string | number)[]>({ default: () => [] })

const uid = useId()

const MAX_VISIBLE_TAGS = 2

const isOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const optionRefs = ref<HTMLLIElement[]>([])
const activeIndex = ref(-1)

const isSelected = (value: string | number) => modelValue.value.includes(value)

const selectedOptions = computed(() =>
  props.options.filter(option => isSelected(option.value)),
)
const visibleTags = computed(() => selectedOptions.value.slice(0, MAX_VISIBLE_TAGS))
const hiddenCount = computed(() => selectedOptions.value.length - visibleTags.value.length)

const onOpen = () => {
  isOpen.value = true

  const firstSelectedIndex = props.options.findIndex(option => isSelected(option.value))
  const fallbackIndex = props.options.length ? 0 : -1
  activeIndex.value = firstSelectedIndex >= 0 ? firstSelectedIndex : fallbackIndex
}

const onClose = (focus = true) => {
  isOpen.value = false
  activeIndex.value = -1

  if (focus) triggerRef.value?.focus()
}

const onToggle = (option: IUiMultiselectOption) => {
  if (props.disabled) return

  modelValue.value = isSelected(option.value)
    ? modelValue.value.filter(value => value !== option.value)
    : [...modelValue.value, option.value]
}

const onRemove = (option: IUiMultiselectOption) => {
  if (props.disabled) return

  modelValue.value = modelValue.value.filter(value => value !== option.value)
}

const onRemoveHidden = () => {
  if (props.disabled) return

  modelValue.value = visibleTags.value.map(option => option.value)
}

const onKeydown = (e: KeyboardEvent) => {
  if (props.disabled) return
  if (e.target !== e.currentTarget) return
  if (e.key === 'Escape' && isOpen.value) return onClose()
  if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) return

  e.preventDefault()

  if (!isOpen.value) return onOpen()

  const currentIndex = activeIndex.value

  if (e.key === 'ArrowDown' && currentIndex < props.options.length - 1) activeIndex.value = currentIndex + 1
  if (e.key === 'ArrowUp' && currentIndex > 0) activeIndex.value = currentIndex - 1
  if ((e.key === 'Enter' || e.key === ' ') && props.options[currentIndex]) onToggle(props.options[currentIndex])
}

onClickOutside(rootRef, () => onClose(false))

watch(activeIndex, async (index) => {
  if (index < 0) return
  await nextTick()

  optionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})
</script>

<template>
  <div
    ref="rootRef"
    :class="[
      'uiMultiselect',
      { 'uiMultiselect--open': isOpen, 'uiMultiselect--disabled': disabled }
    ]"
  >
    <span
      v-if="label"
      class="uiMultiselect__label"
    >
      {{ label }}
    </span>

    <div
      ref="triggerRef"
      class="uiMultiselect__trigger"
      role="combobox"
      aria-haspopup="listbox"
      :tabindex="disabled ? -1 : 0"
      :aria-label="label"
      :aria-expanded="isOpen"
      :aria-disabled="disabled"
      :aria-activedescendant="activeIndex >= 0 ? `${uid}-${activeIndex}` : undefined"
      @click="isOpen ? onClose(false) : onOpen()"
      @keydown="onKeydown"
    >
      <span
        v-if="!selectedOptions.length"
        class="uiMultiselect__placeholder"
      >
        {{ placeholder }}
      </span>

      <div
        v-else
        class="uiMultiselect__tags"
      >
        <span
          v-for="option in visibleTags"
          :key="option.value"
          class="uiMultiselect__tag"
        >
          {{ option.label }}

          <button
            type="button"
            class="uiMultiselect__tagRemove"
            :disabled="disabled"
            :aria-label="$t('common.multiselect.remove', { label: option.label })"
            @click.stop="onRemove(option)"
          />
        </span>

        <span
          v-if="hiddenCount"
          class="uiMultiselect__tag"
        >
          +{{ hiddenCount }}

          <button
            type="button"
            class="uiMultiselect__tagRemove"
            :disabled="disabled"
            :aria-label="$t('common.multiselect.removeRest', { count: hiddenCount })"
            @click.stop="onRemoveHidden"
          />
        </span>
      </div>

      <button
        v-if="selectedOptions.length"
        type="button"
        class="uiMultiselect__tagRemove"
        :disabled="disabled"
        :aria-label="$t('common.multiselect.clear')"
        @click.stop="modelValue = []"
      />

      <span
        class="uiMultiselect__chevron"
        aria-hidden="true"
      />
    </div>

    <div
      v-if="isOpen"
      class="uiMultiselect__listWrapper"
    >
      <ul
        class="uiMultiselect__list"
        role="listbox"
        aria-multiselectable="true"
        :aria-label="label"
      >
        <li
          v-for="(option, index) in options"
          :id="`${uid}-${index}`"
          ref="optionRefs"
          :key="option.value"
          :class="[
            'uiMultiselect__option',
            {
              'uiMultiselect__option--active': index === activeIndex,
              'uiMultiselect__option--selected': isSelected(option.value),
            },
          ]"
          role="option"
          :aria-selected="isSelected(option.value)"
          @click="onToggle(option)"
          @mouseenter="activeIndex = index"
        >
          <span
            class="uiMultiselect__checkbox"
            aria-hidden="true"
          />

          <span class="uiMultiselect__optionLabel">
            {{ option.label }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.uiMultiselect {
  $root: &;

  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  width: 100%;

  &__label {
    @include body-3-medium;
    color: $text-400;
  }

  &__trigger {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    min-height: 4.8rem;
    padding: 0.6rem 1.6rem;
    background-color: $white;
    border: 0.1rem solid $text-300;
    border-radius: 1.6rem;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &:hover {
      border-color: $text-400;
    }

    &:focus-visible {
      border-color: $purple-400;
      outline: 0.2rem solid $purple-400;
      outline-offset: 0.2rem;
    }
  }

  &__placeholder {
    @include body-2;
    flex: 1;
    color: $text-200;
    text-align: left;
  }

  &__tags {
    display: flex;
    flex: 1;
    flex-wrap: nowrap;
    align-items: center;
    gap: 0.8rem;
    overflow: hidden;
  }

  &__tag {
    @include body-3;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.8rem;
    padding: 0.4rem 0.8rem;
    color: $text-400;
    white-space: nowrap;
    background-color: $gray-200;
    border-radius: 0.8rem;
  }

  &__tagRemove {
    flex-shrink: 0;
    width: 1.6rem;
    height: 1.6rem;
    background-color: transparent;
    background-image: url('/icons/icn_close.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    border: none;
    cursor: pointer;
  }

  &__chevron {
    flex-shrink: 0;
    width: 2.4rem;
    height: 2.4rem;
    background-image: url('/icons/icon_arrow_down.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transition: transform 0.2s ease;
  }

  &__listWrapper {
    position: absolute;
    top: calc(100% + 0.4rem);
    left: 0;
    z-index: 10;
    width: 100%;
    overflow: hidden;
    background-color: $white;
    border-radius: 1.6rem;
    box-shadow: 0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.08);
  }

  &__list {
    width: 100%;
    max-height: 24rem;
    margin: 0;
    padding: 0.8rem;
    overflow-y: auto;
    list-style: none;

    @include thin-scrollbar;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.8rem 1.6rem;
    border-radius: 0.8rem;
    cursor: pointer;

    &--active,
    &:hover {
      background-color: $gray-100;
    }

    &--selected {
      background-color: $gray-100;

      #{$root}__checkbox::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url('/icons/components/UiMultiselect/check.svg');
        background-repeat: no-repeat;
        background-position: center;
        background-size: 1.2rem 1rem;
      }
    }
  }

  &__checkbox {
    position: relative;
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    background-color: $white;
    border: 0.15rem solid $base-50;
    border-radius: 0.4rem;
  }

  &__optionLabel {
    @include body-2;
    color: $text-400;
  }

  &--open {
    #{$root}__trigger {
      border-color: $purple-400;
    }

    #{$root}__chevron {
      transform: rotate(180deg);
    }
  }

  &--disabled {
    cursor: not-allowed;

    #{$root}__trigger {
      pointer-events: none;
    }

    #{$root}__label,
    #{$root}__placeholder {
      color: $text-200;
    }
  }
}
</style>
